// OperationReport47Component.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import { Loading } from "../../share/loading";
import "../../share/form.css";
import { Formik, Field, Form, FieldArray } from "formik";
import Swal from "sweetalert2";
import Select from "react-select";
import { GetVMdDept } from "../../share/api/MasterDataApi";

import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* ===== locales ===== */
registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

/* ===== constants & helpers ===== */
const TH_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const YEARS = range(1963, new Date().getFullYear() + 8);

const N = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
const fmtMoney = (n) =>
  Number(String(n ?? 0).replace(/,/g, "")).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const toThaiDateLabel = (v) => {
  if (!v) return "-";
  if (typeof v === "string" && /\s[ก-๙]/.test(v)) return v; // already TH label
  const d = typeof v === "string" ? parseISO(v) : v;
  if (!(d instanceof Date) || isNaN(d)) return "-";
  const be = addYears(543, d);
  const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "d")} ${monthName} ${format(be, "yyyy")}`;
};

const formatMonthYearBE = (date) => {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
};

/** พยายาม parse วันที่ไทยรูปแบบ "1 มกราคม 2567" -> Date (คริสต์ศักราช) */
const parseThaiDateLoose = (s) => {
  if (!s || typeof s !== "string") return null;
  const parts = s.trim().split(/\s+/);
  if (parts.length < 3) return null;
  const [dStr, mStr, yStr] = parts;
  const d = parseInt(dStr.replace(/\D/g, ""), 10);
  const monthIdx = TH_MONTHS.findIndex((m) => m === mStr);
  const be = parseInt(yStr.replace(/\D/g, ""), 10);
  if (isNaN(d) || monthIdx < 0 || isNaN(be)) return null;
  const ad = be - 543;
  const dt = new Date(ad, monthIdx, d);
  return isNaN(dt) ? null : dt;
};

/* ===== PDF Thai font (optional) ===== */
function base64FromArrayBuffer(buf) {
  const b = new Uint8Array(buf);
  const c = 0x8000;
  let s = "";
  for (let i = 0; i < b.length; i += c) {
    s += String.fromCharCode.apply(null, b.subarray(i, i + c));
  }
  return btoa(s);
}
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([
    fetch("/fonts/Sarabun-Regular.ttf"),
    fetch("/fonts/Sarabun-Bold.ttf"),
  ]);
  const [regBuf, boldBuf] = await Promise.all([
    regRes.arrayBuffer(),
    boldRes.arrayBuffer(),
  ]);
  const regB64 = base64FromArrayBuffer(regBuf);
  const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push([
    "addFonts",
    function () {
      this.addFileToVFS("Sarabun-Regular.ttf", regB64);
      this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
      this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
      this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
    },
  ]);
  new jsPDF();
  thaiFontLoaded = true;
}

/* ===== Component ===== */
export function OperationReport47Component() {
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const [loading, setLoading] = useState(false);

  const [deptsData, setDeptsData] = useState([]); // [{value,label}]
  const [reportUsingMoney, setReportUsingMoney] = useState([]); // raw from API

  // filters (หน่วยงาน + เดือน/ปี)
  const [deptIdFilter, setDeptIdFilter] = useState("all");
  const [reportMonth, setReportMonth] = useState("");
  const [reportMonthDate, setReportMonthDate] = useState(null);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // 10/20/50/100

  useEffect(() => {
    GetVMdDept({ setDeptsData, setLoading });
    GetReportUsingMoney();
  }, []);

  useEffect(() => {
    if (!reportMonth) {
      setReportMonthDate(null);
      return;
    }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  const GetReportUsingMoney = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/Support/reportUsingMoney", {
        method: "POST",
        headers: {
          token: user.token,
          "Content-type": "application/json",
        },
      });
      const resJson = await res.json();
      const filterWaitApprove = (resJson?.dataenum || []).filter(
        (item) => String(item?.supportStatusName || "").endsWith("4-7")
      );
      setReportUsingMoney(filterWaitApprove);
      setPage(1);
    } catch (e) {
      console.error(e);
      Swal.fire("ดึงข้อมูลไม่สำเร็จ", "", "error");
    } finally {
      setLoading(false);
    }
  };

  // apply filters (client-side)
  const filtered = useMemo(() => {
    let data = reportUsingMoney;

    if (deptIdFilter && deptIdFilter !== "all") {
      data = data.filter((it) => String(it?.deptId) === String(deptIdFilter));
    }

    if (reportMonth) {
      // filter by paymentDate month/year
      const [y, m] = reportMonth.split("-").map(Number);
      data = data.filter((it) => {
        const t = parseThaiDateLoose(it?.paymentDate);
        if (!t) return false;
        return t.getFullYear() === y && t.getMonth() + 1 === m;
      });
    }

    return data;
  }, [reportUsingMoney, deptIdFilter, reportMonth]);

  // pagination derived
  const totalRows = filtered.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // formik init (ส่งรับทราบเฉพาะรายการที่อยู่ใน filtered ทั้งหมด)
  const dataInitialValuesforofficer = {
    reportSelectedAr: filtered.map((item) => ({
      resultKnowledgeId: item?.resultKnowledgeId,
      resultDsdId: item?.resultDsdId,
    })),
    deptId: deptIdFilter,
  };

  const onSubmit = async () => {
    Swal.fire({
      icon: "info",
      title: "ยืนยันการบันทึกข้อมูลหรือไม่?",
      text: "",
      confirmButtonColor: "#0A66C2",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
      cancelButtonColor: "#d33",
      showCancelButton: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          let res = await fetch("/api/Support/considerReportUsingMoney", {
            method: "POST",
            headers: {
              token: user.token,
              "Content-type": "application/json",
            },
            body: JSON.stringify({
              sysName: "4-8",
              considerReportResults: filtered, // ใช้ชุดข้อมูลที่ผ่านการกรอง
            }),
          });
          let resJson = await res.json();
          if (resJson.status === 1) {
            Swal.fire({
              icon: "success",
              title: "บันทึกข้อมูลสำเร็จ",
              confirmButtonColor: "#0A66C2",
              confirmButtonText: "ตกลง",
            }).then((r) => {
              if (r.isConfirmed) window.location.reload();
            });
          } else {
            Swal.fire("บันทึกไม่สำเร็จ", resJson?.message || "", "error");
          }
        } catch (e) {
          console.error(e);
          Swal.fire("บันทึกไม่สำเร็จ", "", "error");
        }
      }
    });
  };

  /* ========================= Export: Excel ========================= */
  const buildAoaAndMerges = (rows) => {
    const aoa = [
      [
        "ลำดับ",
        "สาขาอาชีพ",
        "สาขา",
        "จำนวนผู้เข้ารับการประเมิน (คน)",
        "จำนวนผู้ผ่านการประเมิน (คน)",
        "จำนวนเงิน ช่วยเหลือ หรืออุดหนุน",
        "", "", "", "", // 5 sub cols
        "วันที่จ่ายเงิน ช่วยเหลือหรือ อุดหนุน",
      ],
      [
        "", "", "", "", "",
        "ค่าตอบแทนผู้ประเมิน (บาท)",
        "ค่าตอบแทนการปฏิบัติงานนอกเวลาราชการ (บาท)",
        "ค่าเดินทางไปราชการทำหน้าที่ผู้ประเมิน (บาท)",
        "ค่าวัสดุใช้ในการประเมิน (บาท)",
        "รวม (บาท)",
        "",
      ],
    ];

    const merges = [
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
      { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } },
      { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } },
      { s: { r: 0, c: 4 }, e: { r: 1, c: 4 } },
      { s: { r: 0, c: 5 }, e: { r: 0, c: 9 } }, // group 5 money cols
      { s: { r: 0, c: 10 }, e: { r: 1, c: 10 } },
    ];

    rows.forEach((item, idx) => {
      const sumAmt =
        N(item.evaluatorExpense) +
        N(item.compensationExpense) +
        N(item.travellingExpense) +
        N(item.materialExpense);

      aoa.push([
        idx + 1,
        item.lawOccupationName || "-",
        item.testOccupationName || "-",
        N(item.personTest),
        N(item.passEvaluation),
        N(item.evaluatorExpense),
        N(item.compensationExpense),
        N(item.travellingExpense),
        N(item.materialExpense),
        sumAmt,
        item.paymentDate || "-",
      ]);
    });

    return { aoa, merges };
  };

  const handleExportExcel = () => {
    if (!filtered.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }
    const { aoa, merges } = buildAoaAndMerges(filtered);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;
    ws["!cols"] = [
      { wch: 8 }, { wch: 28 }, { wch: 24 },
      { wch: 16 }, { wch: 18 },
      { wch: 24 }, { wch: 30 }, { wch: 30 }, { wch: 22 }, { wch: 16 },
      { wch: 26 },
    ];

    // summary row
    const r = aoa.length;
    const totalEvaluator = filtered.reduce((s, x) => s + N(x.evaluatorExpense), 0);
    const totalComp = filtered.reduce((s, x) => s + N(x.compensationExpense), 0);
    const totalTravel = filtered.reduce((s, x) => s + N(x.travellingExpense), 0);
    const totalMaterial = filtered.reduce((s, x) => s + N(x.materialExpense), 0);
    const totalSum = totalEvaluator + totalComp + totalTravel + totalMaterial;
    const totalPerson = filtered.reduce((s, x) => s + N(x.personTest), 0);
    const totalPass = filtered.reduce((s, x) => s + N(x.passEvaluation), 0);

    ws[XLSX.utils.encode_cell({ r, c: 0 })] = { t: "s", v: "รวม" };
    ws[XLSX.utils.encode_cell({ r, c: 3 })] = { t: "n", v: totalPerson };
    ws[XLSX.utils.encode_cell({ r, c: 4 })] = { t: "n", v: totalPass };
    ws[XLSX.utils.encode_cell({ r, c: 5 })] = { t: "n", v: totalEvaluator };
    ws[XLSX.utils.encode_cell({ r, c: 6 })] = { t: "n", v: totalComp };
    ws[XLSX.utils.encode_cell({ r, c: 7 })] = { t: "n", v: totalTravel };
    ws[XLSX.utils.encode_cell({ r, c: 8 })] = { t: "n", v: totalMaterial };
    ws[XLSX.utils.encode_cell({ r, c: 9 })] = { t: "n", v: totalSum };
    ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: 2 } });

    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ.4-7");
    XLSX.writeFile(
      wb,
      `รายงาน_ชอ4-7_${reportMonth || "ทั้งหมด"}_${deptIdFilter}.xlsx`
    );
  };

  /* ========================= Export: PDF ========================= */
  const handleExportPDF = async () => {
    if (!filtered.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }
    await ensureThaiFontLoaded();

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFont("Sarabun", "bold"); doc.setFontSize(16);
    doc.text(
      `ศูนย์ประเมินความรู้ความสามารถกลาง — ชอ.4-7 — ${formatMonthYearBE(reportMonthDate) || "ทุกเดือน"}`,
      40, 40
    );

    const head = [
      [
        { content: "ลำดับ", rowSpan: 2 },
        { content: "สาขาอาชีพ", rowSpan: 2 },
        { content: "สาขา", rowSpan: 2 },
        { content: "จำนวนผู้เข้ารับการประเมิน (คน)", rowSpan: 2 },
        { content: "จำนวนผู้ผ่านการประเมิน (คน)", rowSpan: 2 },

        { content: "จำนวนเงิน ช่วยเหลือ หรืออุดหนุน", colSpan: 5 },

        { content: "วันที่จ่ายเงิน ช่วยเหลือหรือ อุดหนุน", rowSpan: 2 },
      ],
      [
        { content: "ค่าตอบแทนผู้ประเมิน (บาท)" },
        { content: "ค่าตอบแทนการปฏิบัติงานนอกเวลาราชการ (บาท)" },
        { content: "ค่าเดินทางไปราชการทำหน้าที่ผู้ประเมิน (บาท)" },
        { content: "ค่าวัสดุใช้ในการประเมิน (บาท)" },
        { content: "รวม (บาท)" },
      ],
    ];

    const body = filtered.map((item, idx) => {
      const sumAmt =
        N(item.evaluatorExpense) +
        N(item.compensationExpense) +
        N(item.travellingExpense) +
        N(item.materialExpense);
      return [
        idx + 1,
        item.lawOccupationName || "-",
        item.testOccupationName || "-",
        fmtMoney(N(item.personTest)),
        fmtMoney(N(item.passEvaluation)),

        fmtMoney(N(item.evaluatorExpense)),
        fmtMoney(N(item.compensationExpense)),
        fmtMoney(N(item.travellingExpense)),
        fmtMoney(N(item.materialExpense)),
        fmtMoney(sumAmt),

        item.paymentDate || "-",
      ];
    });

    doc.setFont("Sarabun", "normal");
    doc.autoTable({
      head,
      body,
      startY: 60,
      styles: { font: "Sarabun", fontSize: 10, cellPadding: 4, halign: "center", overflow: "linebreak" },
      headStyles: { fillColor: [255, 236, 224], textColor: [0, 0, 0], font: "Sarabun", fontStyle: "bold" },
      columnStyles: {
        1: { halign: "left" }, 2: { halign: "left" },
        3: { halign: "right" }, 4: { halign: "right" },
        5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" },
        10: { halign: "center" },
      },
      didDrawPage: (data) => {
        const n = doc.internal.getNumberOfPages();
        doc.setFont("Sarabun", "normal"); doc.setFontSize(9);
        doc.text(`หน้า ${data.pageNumber} / ${n}`, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
      },
    });

    // summary
    const totalEvaluator = filtered.reduce((s, x) => s + N(x.evaluatorExpense), 0);
    const totalComp = filtered.reduce((s, x) => s + N(x.compensationExpense), 0);
    const totalTravel = filtered.reduce((s, x) => s + N(x.travellingExpense), 0);
    const totalMaterial = filtered.reduce((s, x) => s + N(x.materialExpense), 0);
    const totalSum = totalEvaluator + totalComp + totalTravel + totalMaterial;
    const totalPerson = filtered.reduce((s, x) => s + N(x.personTest), 0);
    const totalPass = filtered.reduce((s, x) => s + N(x.passEvaluation), 0);

    const y = (doc.lastAutoTable?.finalY || 60) + 24;
    doc.setFont("Sarabun", "bold"); doc.setFontSize(12);
    doc.text(
      `รวม: ผู้เข้ารับฯ ${fmtMoney(totalPerson)} คน | ผ่านฯ ${fmtMoney(totalPass)} คน | ` +
      `ผู้ประเมิน ${fmtMoney(totalEvaluator)} | นอกเวลา ${fmtMoney(totalComp)} | เดินทาง ${fmtMoney(totalTravel)} | วัสดุ ${fmtMoney(totalMaterial)} | รวมเงิน ${fmtMoney(totalSum)} บาท`,
      40, y
    );

    doc.save(`รายงาน_ชอ4-7_${reportMonth || "all"}_${deptIdFilter}.pdf`);
  };

  return (
    <Formik initialValues={dataInitialValuesforofficer} onSubmit={onSubmit} enableReinitialize>
      {(formik) => {
        return (
          <Container className="py-3">
            <Form>

              {/* Filters */}
              <Row className="g-3 align-items-end">
                <Col md={6}>
                  <Label>ชื่อหน่วยงาน</Label>
                  <Select
                    isSearchable
                    styles={{
                      control: (b) => ({ ...b, borderRadius: "20px" }),
                      singleValue: (b) => ({ ...b, color: "#0A66C2" }),
                    }}
                    options={[{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]}
                    value={
                      [{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]
                        .find((o) => String(o.value) === String(deptIdFilter)) || null
                    }
                    onChange={(opt) => { setDeptIdFilter(opt?.value ?? "all"); setPage(1); }}
                    placeholder={<div className="input-color text-center">กรุณาเลือกหน่วยงาน</div>}
                    className="rounded-pill text-center input-color"
                  />
                </Col>

                <Col md={6}>
                  <Label>เดือน/ปี ที่จ่ายเงิน</Label>
                  <DatePicker
                    selected={reportMonthDate}
                    onChange={(date) => {
                      if (date instanceof Date && !isNaN(date)) {
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, "0");
                        setReportMonth(`${y}-${m}`);
                        setReportMonthDate(new Date(y, Number(m) - 1, 1));
                      } else {
                        setReportMonth("");
                        setReportMonthDate(null);
                      }
                      setPage(1);
                    }}
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    showFullMonthYearPicker
                    locale="th-buddhist"
                    placeholderText="เลือกเดือน/ปี (พ.ศ.)"
                    value={formatMonthYearBE(reportMonthDate)}
                    renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                      <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                        <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <select className="form-select form-select-sm" style={{ width: 140 }} value={TH_MONTHS[getMonth(date)]} onChange={({ target: { value } }) => changeMonth(TH_MONTHS.indexOf(value))}>
                            {TH_MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <select className="form-select form-select-sm" style={{ width: 100 }} value={getYear(date)} onChange={({ target: { value } }) => changeYear(Number(value))}>
                            {YEARS.map((y) => <option key={y} value={y}>{y + 543}</option>)}
                          </select>
                        </div>
                        <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
                      </div>
                    )}
                    customInput={
                      <input
                        className="form-control rounded-pill text-center input-color"
                        readOnly
                        value={formatMonthYearBE(reportMonthDate)}
                        placeholder="เลือกเดือน/ปี (พ.ศ.)"
                      />
                    }
                  />
                </Col>

                <Col md="auto" className="pt-2 d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-outline-success" onClick={handleExportExcel}>ส่งออก Excel</button>
                  <button type="button" className="btn btn-outline-danger" onClick={handleExportPDF}>ส่งออก PDF</button>
                </Col>
              </Row>

              <div className="mt-3">
                <Table bordered responsive>
                  <thead className="table-light">
                    <tr>
                      <th rowSpan="2">ลำดับ</th>
                      <th rowSpan="2">สาขาอาชีพ</th>
                      <th rowSpan="2">สาขา</th>
                      <th rowSpan="2">จำนวนผู้เข้ารับการประเมิน (คน)</th>
                      <th rowSpan="2">จำนวนผู้ผ่านการประเมิน (คน)</th>
                      <th colSpan="5">จำนวนเงิน ช่วยเหลือ หรืออุดหนุน</th>
                      <th rowSpan="2">วันที่จ่ายเงิน ช่วยเหลือหรือ อุดหนุน</th>
                    </tr>
                    <tr>
                      <th>ค่าตอบแทนผู้ประเมิน (บาท)</th>
                      <th>ค่าตอบแทนการปฏิบัติงานนอกเวลาราชการ (บาท)</th>
                      <th>ค่าเดินทางไปราชการทำหน้าที่ผู้ประเมิน (บาท)</th>
                      <th>ค่าวัสดุใช้ในการประเมิน (บาท)</th>
                      <th>รวม (บาท)</th>
                    </tr>
                  </thead>

                  {!loading ? (
                    <FieldArray name="datatable">
                      {() => {
                        return (
                          <tbody>
                            {pageData && pageData.length > 0 ? (
                              pageData.map((item, i) => {
                                const idx = (page - 1) * pageSize + i + 1;
                                const sumAmt =
                                  N(item.evaluatorExpense) +
                                  N(item.compensationExpense) +
                                  N(item.travellingExpense) +
                                  N(item.materialExpense);
                                return (
                                  <tr key={idx}>
                                    <td className="text-center">{idx}</td>
                                    <td className="text-center">{item.lawOccupationName || "-"}</td>
                                    <td className="text-center">{item.testOccupationName || "-"}</td>
                                    <td className="text-center">{fmtMoney(N(item.personTest))}</td>
                                    <td className="text-center">{fmtMoney(N(item.passEvaluation))}</td>
                                    <td className="text-center">{fmtMoney(N(item.evaluatorExpense))}</td>
                                    <td className="text-center">{fmtMoney(N(item.compensationExpense))}</td>
                                    <td className="text-center">{fmtMoney(N(item.travellingExpense))}</td>
                                    <td className="text-center">{fmtMoney(N(item.materialExpense))}</td>
                                    <td className="text-center">{fmtMoney(sumAmt)}</td>
                                    <td className="text-center">{item.paymentDate || "-"}</td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td className="text-center" colSpan="11">ไม่มีข้อมูล</td>
                              </tr>
                            )}
                          </tbody>
                        );
                      }}
                    </FieldArray>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan="11">
                          <Loading />
                        </td>
                      </tr>
                    </tbody>
                  )}
                </Table>
              </div>

              {/* Pagination */}
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                <div className="d-flex align-items-center gap-2">
                  <span>แสดงต่อหน้า:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 100 }}
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  >
                    {[10, 20, 50, 100].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <span className="ms-2">ทั้งหมด {totalRows.toLocaleString()} รายการ</span>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(1)}>{'«'}</button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>{'‹'}</button>
                  <span>หน้า {page} / {pageCount}</span>
                  <button className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>{'›'}</button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount} onClick={() => setPage(pageCount)}>{'»'}</button>
                </div>
              </div>

              {/* <div className="d-flex justify-content-center mt-4">
                <button
                  disabled={filtered.length === 0}
                  type="submit"
                  className="btn-blue p-2 w-25 align-items-center text-center"
                >
                  รับทราบ
                </button>
              </div> */}
            </Form>
          </Container>
        );
      }}
    </Formik>
  );
}
